/**
 * Normalisation des préfixes de locale dans les pathnames.
 *
 * Le router client ne monte les routes que sous les préfixes canoniques
 * (/en, /es, … — jamais /fr, la locale par défaut vit à la racine). Toute
 * variante de préfixe arrive donc avec un basename '' et tombe dans le
 * catch-all 404 au lieu de la page attendue :
 *   - /fr/login        → préfixe explicite de la locale par défaut
 *   - /EN/login        → variante de casse
 *   - /en-US/login     → tag BCP 47 régional (ce que produit navigator.language)
 *   - /pt-BR/panier    → idem
 *
 * Module volontairement pur (zéro import) : la liste des locales est passée
 * en paramètre par l'appelant (i18n.ts), et le test unitaire s'exécute sous
 * node:test sans tirer les effets de bord d'i18n.ts (chargement des catalogues).
 */

/** Premier segment « locale-like » : 2 lettres + région optionnelle (US, BR, 419, Hant…) */
const LOCALE_PREFIX_RE = /^\/([a-zA-Z]{2})(-[a-zA-Z0-9]{2,4})?(\/.*)?$/

/**
 * Retourne le pathname canonique si le premier segment est un préfixe de
 * locale supporté mais mal formé, sinon null (rien à corriger).
 *
 * Les codes inconnus (/xx/login) ne sont PAS corrigés : vrai 404, et zéro
 * risque de collision avec un slug court de page de contenu.
 */
export function normalizeLocalePathname(
  pathname: string,
  supportedLocales: string[],
  defaultLocale: string,
): string | null {
  const m = pathname.match(LOCALE_PREFIX_RE)
  if (!m) return null
  const base = m[1].toLowerCase()
  if (!supportedLocales.includes(base)) return null
  const rest = m[3] && m[3] !== '/' ? m[3] : ''
  const canonical = ((base === defaultLocale ? '' : `/${base}`) + rest) || '/'
  return canonical === pathname ? null : canonical
}

/**
 * Locale portée par le préfixe d'un chemin (`/en/…` → `en`), sinon la locale par
 * défaut. Accepte une URL COMPLÈTE : le SSR passe `/en?gclid=…`, et sans retirer la
 * query « en » n'était pas suivi de « / » → page rendue en français, base `/`, route
 * introuvable = page vide côté serveur (Google voyait une page blanche).
 */
export function localeFromPath(pathname: string, supported: string[], defaultLocale: string): string {
  const path = pathname.split(/[?#]/)[0]
  const match = path.match(/^\/([a-z]{2})(\/|$)/)
  if (match && match[1] !== defaultLocale && supported.includes(match[1])) return match[1]
  return defaultLocale
}
